import * as M from '@mantine/core';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  AI_AGENT_TASK_STATUSES,
  manualUploadChannelOptions,
  taskStatusOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';
import { InfoField } from '../../../../../../ui/infoField/InfoField';
import CustomSearch from '../../../../../../ui/search/customSearch';

import type { KeyboardEvent, MouseEvent, RefObject } from 'react';
import type { IModelOption } from '../../../Common/hooks/useModelOptions';
import type { AiAgentAsCsFileSearchType, IAiAgentAsCsFileFilterForm } from '../types';

interface IAiAgentAsCsFileListFilterSectionProps {
  filterForm: IAiAgentAsCsFileFilterForm;
  modelOptions: IModelOption[];
  searchType: AiAgentAsCsFileSearchType;
  inputRef: RefObject<HTMLInputElement>;
  onChangeFilterForm: (nextFilter: Partial<IAiAgentAsCsFileFilterForm>) => void;
  onChangeSearchType: (value: string | null) => void;
  handleSearch: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => void;
  resetSearchInput: () => void;
  onReset: () => void;
}

export default function AiAgentAsCsFileListFilterSection(props: IAiAgentAsCsFileListFilterSectionProps) {
  const {
    filterForm,
    modelOptions,
    searchType,
    inputRef,
    onChangeFilterForm,
    onChangeSearchType,
    handleSearch,
    resetSearchInput,
    onReset,
  } = props;

  const handleServiceChannel = (value: IAiAgentAsCsFileFilterForm['serviceChannels'][number], checked: boolean) => {
    const serviceChannels = checked
      ? [...filterForm.serviceChannels, value]
      : filterForm.serviceChannels.filter((channel) => channel !== value);

    onChangeFilterForm({ serviceChannels });
  };

  const handleTaskStatus = (value: IAiAgentAsCsFileFilterForm['taskStatuses'][number], checked: boolean) => {
    const taskStatuses = checked
      ? [...filterForm.taskStatuses, value]
      : filterForm.taskStatuses.filter((status) => status !== value);

    onChangeFilterForm({ taskStatuses });
  };

  return (
    <M.Box
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        borderLeft: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <M.SimpleGrid cols={2} spacing={0} verticalSpacing={0} style={{ gridTemplateColumns: '180px minmax(0, 1fr)' }}>
        <InfoField label="등록 서비스" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" align="center" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.serviceChannels.length === 0}
              onChange={() => onChangeFilterForm({ serviceChannels: [] })}
            />
            {manualUploadChannelOptions
              .filter((option) => option.value !== AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL)
              .map((option) => (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.serviceChannels.includes(option.value)}
                  onChange={(event) => handleServiceChannel(option.value, event.currentTarget.checked)}
                />
              ))}
          </M.Flex>
        </InfoField>
        <InfoField label="변환 상태" labelAlign="left" contentAlign="left">
          <M.Flex gap="lg" wrap="wrap">
            <M.Checkbox
              label="전체"
              size="sm"
              checked={filterForm.taskStatuses.length === 0}
              onChange={() => onChangeFilterForm({ taskStatuses: [] })}
            />
            {taskStatusOptions
              .filter((option) => option.value !== AI_AGENT_TASK_STATUSES.ALL)
              .map((option) => (
                <M.Checkbox
                  key={option.value}
                  label={option.label}
                  size="sm"
                  checked={filterForm.taskStatuses.includes(option.value)}
                  onChange={(event) => handleTaskStatus(option.value, event.currentTarget.checked)}
                />
              ))}
          </M.Flex>
        </InfoField>
        <InfoField label="기계모델" labelAlign="left" contentAlign="left">
          <M.Select
            data={modelOptions}
            placeholder="모델을 선택해주세요."
            value={filterForm.modelId}
            onChange={(modelId) => onChangeFilterForm({ modelId })}
            searchable
          />
        </InfoField>
        <InfoField label="상세 검색" labelAlign="left" contentAlign="left">
          <M.Flex gap="sm" align="center" wrap="wrap" w="100%">
            <CustomSearch
              resetSearch={onReset}
              resetSearchInput={resetSearchInput}
              searchType={searchType}
              setSearchType={onChangeSearchType}
              inputRef={inputRef}
              handleSearch={handleSearch}
              searchOptions={[
                {
                  value: 'FILE_NAME',
                  label: '파일명',
                },
                {
                  value: 'TEMPLATE_NAME',
                  label: '템플릿명',
                },
              ]}
              placeholder="검색어를 입력하세요."
            />
          </M.Flex>
        </InfoField>
      </M.SimpleGrid>
    </M.Box>
  );
}
